import React, { Component, Fragment } from 'react';

import { colors } from '@lightspeed/cirrus-tokens/src/colors';
import { IconChevronRight } from '@lightspeed/flame/Icon/ChevronRight';
import CirrusTopbar, { Breadcrumbs, Toggler } from 'cirrus/Topbar';
import PropTypes from 'prop-types';

import { createReactRenderer } from 'helpers/react-renderer';
import { decodeHtml } from 'helpers/utils';
import history from 'pages/router/history';
import {
  checkNavigationBlockers,
  checkParcelNavigationBlockers,
  isPathWithParcelNavigationBlocker,
  isUnifiedBlockerEnabled,
} from 'root/apps/utils/navigationBlocker';

import { iconToSectionMap } from './utils.tsx';

export const TopbarReactRootSelector = '[data-react-root="Topbar"]';

const FREE_TIER_MODULE = 'customers.invoicing_integration.free_tier';

const stencilCallback = () => {
  if (window.isGriffin() && !window.hasLocalizejsModule) {
    window.StencilWebAPI.navigation.updateNavState();
  }
};

export default class Topbar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      crumbs: this.formatCrumbs(props.crumbs),
    };

    this.formatCrumbs = this.formatCrumbs.bind(this);
    this.handleToggler = this.handleToggler.bind(this);
  }

  componentDidMount() {
    window.merchantos.asyncSubscribe.subscribeToFormCall(
      'react.mount.topbar',
      ({ props, callback = () => {} }) => {
        this.setState(
          {
            crumbs: this.formatCrumbs(props.crumbs),
          },
          () => {
            stencilCallback();
            callback();
          }
        );
      }
    );
  }

  formatCrumbs(crumbs = []) {
    return crumbs
      .map((crumb, index) => {
        const isFirst = index === 0;
        const isLast = index === crumbs.length - 1;
        const isSection = crumb.type === 'ui_tab';
        const text = decodeHtml(crumb.text);
        const varText = decodeHtml(crumb.varText);
        const page = crumb.page;
        let href = isSection
          ? `/?form_name=${crumb.type}&tab=${crumb.name}`
          : `/?name=${crumb.name}&form_name=${crumb.type}`;
        href = crumb.path ? crumb.path : href;

        if (!text && !varText) {
          return undefined;
        }

        const performNavigation = (event) => {
          if (page) {
            history.push(page);
          } else if (crumb.type === 'react') {
            window.returnToParentX('react', index);
          } else if (isSection) {
            window.merchantos.tabs.show(crumb.name);
          } else if (crumb.name === 'register' && crumb.type === 'sale') {
            window.merchantos.utils.openUrl('/register.php');
          } else {
            window.returnToParentX(crumb.type, index + 1);
          }
        };

        return Object.assign(
          {
            text,
            varText,
            'data-automation': !isLast ? 'breadcrumb' : 'topbar-page-title',
            'data-automation-id': !isLast ? `breadcrumb_${index + 1}` : undefined,
          },
          isSection || !isLast
            ? {
                href: page || href,
                onClick(e) {
                  e.preventDefault();
                  if (isUnifiedBlockerEnabled()) {
                    // Set the skip flag before checking so that any nested PageRouter.goTo
                    // triggered by performNavigation does not re-prompt the user.
                    window.navigationBlockersSkipNextCheck = true;
                    checkNavigationBlockers()
                      .then(() => performNavigation(e))
                      .catch(() => {
                        // Save failed; the registered blocker surfaces its own error UI. Navigation stays on current page.
                      })
                      .finally(() => {
                        window.navigationBlockersSkipNextCheck = false;
                      });
                  } else {
                    // LEGACY (FF off): parcel-only path. Remove when ui.navigation_blocker.unified is fully on.
                    const hasFreeTierModule = window.lsCloud.Modules.hasModule(FREE_TIER_MODULE);
                    const currentReactPath = history.location && history.location.pathname;
                    const shouldCheckParcelBlockers =
                      hasFreeTierModule &&
                      currentReactPath &&
                      isPathWithParcelNavigationBlocker(currentReactPath);
                    if (shouldCheckParcelBlockers) {
                      window.parcelNavigationBlockersSkipNextCheck = true;
                      checkParcelNavigationBlockers()
                        .then(() => performNavigation(e))
                        .catch(() => {
                          // Save failed; MFE is notified via onSaveError and shows its own toast. Navigation stays on current page.
                        })
                        .finally(() => {
                          window.parcelNavigationBlockersSkipNextCheck = false;
                        });
                    } else {
                      performNavigation(e);
                    }
                  }
                },
              }
            : {},
          isFirst
            ? {
                icon: iconToSectionMap((crumb.icon || text).toLowerCase()),
              }
            : {},
          window.isGriffin()
            ? {
                className: `js-${isLast ? 'page-title' : 'breadcrumb'}`,
                parentIndex: index + 1,
              }
            : {}
        );
      })
      .filter((crumb) => !!crumb);
  }

  handleToggler(event) {
    event.preventDefault();
    event.stopPropagation();
    window.merchantos.sidebar.toggle();
  }

  render() {
    const { crumbs } = this.state;

    return (
      <Fragment>
        <CirrusTopbar>
          <Toggler onClick={(event) => this.handleToggler(event)} id="sidebar-toggler" />
          <Breadcrumbs
            crumbs={crumbs}
            separator={<IconChevronRight size="0.75rem" baseColor={colors.seal} />}
            maxCrumbs={4}
          />
        </CirrusTopbar>
      </Fragment>
    );
  }
}

Topbar.propTypes = {
  crumbs: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      text: PropTypes.node,
      varText: PropTypes.node,
      href: PropTypes.string,
    })
  ).isRequired,
};

Topbar.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  crumbs: [
    {
      text: document.getElementById('topbar-override-title')
        ? document.getElementById('topbar-override-title').innerText
        : 'Lightspeed Retail',
    },
  ],
};

const RenderedComponent = createReactRenderer({
  component: Topbar,
  selector: TopbarReactRootSelector,
});

RenderedComponent.mount();

const tempSubscription = window.merchantos.asyncSubscribe.subscribeToFormCall(
  'react.mount.topbar',
  ({ props, callback = () => {} }) => {
    RenderedComponent.mount({
      props,
      callback: () => {
        tempSubscription.unsubscribe();
        stencilCallback();
        callback();
      },
    });
  }
);
